/** @format */

import { PLATFORM_ID, afterRenderEffect, inject, type Signal } from '@angular/core';

import { isPlatformBrowser } from '@angular/common';

export interface FocusRestore {
  // Get the element that currently has focus, before a transient disable.
  capture(): void;
}

export interface FocusRestoreOptions {
  /**
   * Called after re-render when the captured element no longer exists in the DOM. Lets the caller
   * relocate focus (e.g. to an accordion header).
   */
  onMissing?: () => void;
}

/**
 * Re-focuses the previously focused element once `ready` becomes true again.
 *
 * HTML elements like checkboxes and native `<button [disabled]>` get disabled while a loading skeleton is displayed.
 * Call `capture()` right before emitting action; focus is restored after the control re-renders as interactive.
 * If the captured element is gone after re-render, `options.onMissing` is invoked so the caller can relocate focus.
 *
 * @param ready Emits true when controls are interactive again, e.g. `() => !disabled()`.
 * @param options Optional hook for when the captured element no longer exists.
 */
export function injectFocusRestore(
  ready: Signal<boolean>,
  options?: FocusRestoreOptions,
): FocusRestore {
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  let capturedElement: HTMLElement | null = null;

  afterRenderEffect(() => {
    if (!ready() || !capturedElement) {
      return;
    }
    if (capturedElement.isConnected) {
      capturedElement.focus({ preventScroll: true });
    } else {
      options?.onMissing?.();
    }
    capturedElement = null;
  });

  return {
    capture(): void {
      capturedElement = null;
      if (!isBrowser) {
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      capturedElement = active && active !== document.body ? active : null;
    },
  };
}
